/**
 * DiamondChart - 思考タイプのダイヤモンドチャート (個人・組織で共有)
 *
 * 軸を45度左回転して配置: システム(A)=左上135°, 変化(D)=右上45°,
 * 人(C)=右下315°, 安定(B)=左下225°。背景の目盛りは同心円で描画する。
 */
import React from 'react';
import { AXIS_META, AXIS_MAX, AXIS_BREAKTHROUGH, type AxisKey } from '../utils/gb74Report';

interface DiamondChartProps {
  /** 各軸の値 (0〜maxValue) */
  axis: Record<AxisKey, number>;
  /** データポリゴンの色 (省略時は最大軸の色) */
  primaryAxis?: AxisKey;
  /** 軸の最大値 (この値で半径いっぱい) */
  maxValue?: number;
  /** 基準線(突破ライン)の値。0以下で非表示 */
  baseValue?: number;
  /** 突破アニメーションを表示するか */
  showBreakthrough?: boolean;
  /** 軸ラベル(システム等)をチャート内に描画するか。象限ヘッダーへ移す場合は false */
  showLabels?: boolean;
  /** 軸ラベルの上書き (例: {A:'A 論理', B:'B 着実', ...})。未指定は AXIS_META.label */
  labels?: Partial<Record<AxisKey, string>>;
  /** 単色指定。指定時はポリゴン・頂点・ラベルをこの色に統一 (軸別色を使わない) */
  monoColor?: string;
  /** 描画サイズ(px) */
  size?: number;
}

const ANGLE: Record<AxisKey, number> = { A: 135, D: 45, C: 315, B: 225 };

export const DiamondChart: React.FC<DiamondChartProps> = ({
  axis,
  primaryAxis,
  maxValue = AXIS_MAX,
  baseValue = AXIS_BREAKTHROUGH,
  showBreakthrough = true,
  showLabels = true,
  labels,
  monoColor,
  size = 260,
}) => {
  const axisColor = (k: AxisKey) => monoColor ?? AXIS_META[k].color;
  const axisLabel = (k: AxisKey) => labels?.[k] ?? AXIS_META[k].label;
  const CX = 130, CY = 130;
  const R_MAX = 92;
  const r = (v: number) => (Math.min(v, maxValue) / maxValue) * R_MAX;
  const atAngle = (key: AxisKey, radius: number): [number, number] => {
    const rad = (ANGLE[key] * Math.PI) / 180;
    return [CX + radius * Math.cos(rad), CY - radius * Math.sin(rad)];
  };
  const pt = (key: AxisKey): [number, number] => atAngle(key, r(axis[key] ?? 0));

  // primaryAxis 未指定なら最大軸
  const maxAxis = (Object.keys(axis) as AxisKey[]).reduce((a, b) => (axis[a] >= axis[b] ? a : b), 'A');
  const fillKey = primaryAxis ?? maxAxis;
  const fill = monoColor ?? AXIS_META[fillKey].color;

  const polygon = (['A', 'D', 'C', 'B'] as AxisKey[]).map((k) => pt(k).join(',')).join(' ');
  const baseR = baseValue > 0 ? (Math.min(baseValue, maxValue) / maxValue) * R_MAX : 0;

  return (
    <svg viewBox="-18 -18 296 296" width={size} height={size} style={{ display: 'block' }}>
      {/* 放射軸線 */}
      {(['A', 'D', 'C', 'B'] as AxisKey[]).map((k) => {
        const [ex, ey] = atAngle(k, R_MAX);
        return <line key={k} x1={CX} y1={CY} x2={ex} y2={ey} stroke="#E5E7EB" strokeWidth="0.5" />;
      })}
      {/* 目盛同心円 */}
      {[0.25, 0.5, 0.75].map((f) => (
        <circle key={f} cx={CX} cy={CY} r={R_MAX * f} fill="none" stroke="#F0EEEA" strokeWidth="0.5" />
      ))}
      {/* 基準線(突破ライン) */}
      {baseR > 0 && (
        <circle cx={CX} cy={CY} r={baseR} fill="none" stroke="#D1D5DB" strokeWidth="1" strokeDasharray="4,3" />
      )}
      {/* データポリゴン */}
      <polygon points={polygon} fill={fill} opacity="0.15" stroke={fill} strokeWidth="2" />
      {/* 頂点 */}
      {(['A', 'B', 'C', 'D'] as AxisKey[]).map((k) => {
        const [px, py] = pt(k);
        const isBreak = showBreakthrough && (axis[k] ?? 0) > baseValue && baseValue > 0;
        return (
          <g key={k}>
            {isBreak && (
              <circle cx={px} cy={py} r="11" fill={axisColor(k)} opacity="0.2">
                <animate attributeName="r" values="11;17;11" dur="2s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.2;0.08;0.2" dur="2s" repeatCount="indefinite" />
              </circle>
            )}
            <circle cx={px} cy={py} r={isBreak ? 5 : 4} fill={axisColor(k)} />
          </g>
        );
      })}
      {/* 軸ラベル */}
      {showLabels && (['A', 'B', 'C', 'D'] as AxisKey[]).map((k) => {
        const [lx, ly] = atAngle(k, R_MAX + 20);
        const cos = Math.cos((ANGLE[k] * Math.PI) / 180);
        const anchor = cos > 0.1 ? 'start' : cos < -0.1 ? 'end' : 'middle';
        return (
          <text key={k} x={lx} y={ly} textAnchor={anchor} dominantBaseline="middle"
            fontSize="12" fontWeight="600" fill={axisColor(k)}>{axisLabel(k)}</text>
        );
      })}
    </svg>
  );
};

export default DiamondChart;
